import type { AppDescription, UserFeatures, BuilderActionWithID, BuilderActionsForApp } from "@glide/app-description";
import { getSourceMetadata } from "@glide/common-core/dist/js/components/SerializedApp";
import {
    type TableName,
    areTableNamesEqual,
    type TypeSchema,
    findTable,
    memoizeFindTable,
    type UserProfileTableInfo,
    type SourceMetadata,
    type SchemaInspector,
} from "@glide/type-schema";
import type { EminenceFlags } from "@glide/billing-types";
import { memoizeFunction } from "@glide/support";
import type {
    AppDescriptionContext,
    ExistingAppDescriptionContext,
    RewritingComponentConfiguratorContext,
} from "@glide/function-utils";
import { definedMap } from "@glideapps/ts-necessities";

import { getUserProfileTableInfo } from "../user-profile-info";
import { getFeatureSetting } from "@glide/common-core";

export function makeSimpleSchemaInspector(
    schema: TypeSchema,
    sourceMetadata: readonly SourceMetadata[] | undefined,
    userProfileTableInfo: UserProfileTableInfo | undefined
): SchemaInspector {
    const shouldMemoizeFindTable = getFeatureSetting("memoizeFindTable");
    let findTableImpl: SchemaInspector["findTable"];
    if (shouldMemoizeFindTable) {
        findTableImpl = memoizeFindTable(schema, sourceMetadata);
    } else {
        findTableImpl = r => findTable(schema, r, sourceMetadata);
    }

    return {
        schema,
        userProfileTableInfo,
        sourceMetadata,
        findTable: findTableImpl,
    };
}

export function getBuilderActionsForTable(
    appID: string,
    builderActions: BuilderActionsForApp,
    tableName: TableName
): BuilderActionWithID[] {
    return [...builderActions.entries()]
        .map(([id, i]) => ({ id, ...i }))
        .filter(i => areTableNamesEqual(i.perApp[appID]?.tableName, tableName));
}

function makeSimpleAppDescriptionContextUnmemoized(
    appID: string,
    appDescription: AppDescription,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): ExistingAppDescriptionContext;
function makeSimpleAppDescriptionContextUnmemoized(
    appID: string,
    appDescription: AppDescription | undefined,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): AppDescriptionContext;
function makeSimpleAppDescriptionContextUnmemoized(
    appID: string,
    appDescription: AppDescription | undefined,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): AppDescriptionContext {
    return {
        ...makeSimpleSchemaInspector(
            schema,
            definedMap(appDescription, getSourceMetadata),
            definedMap(appDescription, getUserProfileTableInfo)
        ),
        appID,
        appDescription,
        builderActions: builderActions ?? new Map(),
        userFeatures,
        eminenceFlags,
        webhookIntegrations: [],
        builderComputationModel: undefined,
        getBuilderAction(id) {
            return builderActions?.get(id);
        },
        getBuilderActionsForTable(tableName: TableName) {
            return definedMap(builderActions, a => getBuilderActionsForTable(appID, a, tableName)) ?? [];
        },
        // @ts-expect-error
        getIsRewritingComponentConfiguratorContext() {
            return false;
        },
    };
}

export const makeSimpleAppDescriptionContext = memoizeFunction(
    "makeSimpleAppDescriptionContext",
    makeSimpleAppDescriptionContextUnmemoized
);

export function elevateAppDescriptionContext<T extends AppDescriptionContext>(
    adc: T
): (T & ExistingAppDescriptionContext) | undefined {
    if (adc.appDescription === undefined) return undefined;
    return adc as T & ExistingAppDescriptionContext;
}

export function makeSimpleComponentConfiguratorContext(
    appID: string,
    appDescription: AppDescription,
    builderActions: BuilderActionsForApp,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): RewritingComponentConfiguratorContext {
    const ccc: RewritingComponentConfiguratorContext = {
        ...makeSimpleAppDescriptionContext(appID, appDescription, builderActions, schema, userFeatures, eminenceFlags),
        getBuilderAction: id => builderActions.get(id),
        requireScreen: n => appDescription.screenDescriptions[n] !== undefined,
        requireMenuScreen: () => true,
    };

    return ccc;
}
